import { useParams, useNavigate } from "react-router-dom";
import { ErrorBoundary, Suspense } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { PageLoadingFallback } from "@/components/suspense/loading-fallback";
import { PageErrorFallback } from "@/components/error-boundary/error-fallback";
import { ArticleForm } from "@/components/articles/article-form";
import { PublishSplitView } from "@/components/articles/publish/publish-split-view";
import {
  articleDetailQueryOptions,
  useUpdateArticle,
} from "@/hooks/use-articles";
import { isBlogArticle, isPremiumArticle, type Article } from "@/types/article";
import type { BlogArticleFormValues } from "@/lib/validation/article-schema";

function ArticleEditContent({ article }: { article: Article }) {
  const navigate = useNavigate();
  const updateArticle = useUpdateArticle();

  if (isBlogArticle(article)) {
    const handleBlogSubmit = (data: BlogArticleFormValues) => {
      updateArticle.mutate({
        articleId: article._id,
        title: data.title,
        excerpt: data.excerpt,
        url: data.url,
        thumbnail: data.thumbnail,
        authorInfo: data.authorInfo,
      });
    };

    return (
      <div className="space-y-4 md:space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">아티클 수정</h1>
          <p className="text-muted-foreground leading-normal">
            아티클 정보를 수정합니다.
          </p>
        </div>
        <div className="max-w-2xl">
          <ArticleForm
            defaultValues={{
              ...article,
            }}
            onSubmit={handleBlogSubmit}
            onCancel={() => navigate("/articles")}
            isSubmitting={updateArticle.isPending}
            submitLabel="수정 저장"
          />
        </div>
      </div>
    );
  }

  if (isPremiumArticle(article)) {
    return <PublishSplitView mode={{ kind: "edit", article }} />;
  }

  return null;
}

export default function ArticleEditPage() {
  const { articleId } = useParams<{ articleId: string }>();

  if (!articleId) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">아티클 ID가 필요합니다.</p>
      </div>
    );
  }

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary onReset={reset} fallback={PageErrorFallback}>
          <Suspense fallback={<PageLoadingFallback />}>
            <SuspenseQuery {...articleDetailQueryOptions(articleId)}>
              {({ data }) => <ArticleEditContent article={data} />}
            </SuspenseQuery>
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
